"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  type Provenance,
  ProvenancePill,
} from "../data-display/ProvenancePill";
import "./OrgFieldEditModal.css";

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

type ValueType = "currency" | "percent" | "number" | "text";

export interface OrgFieldEditModalProps {
  /** Field display name shown in the header. */
  label: string;
  /** Optional description paragraph. */
  description?: string;
  /** Current formatted value string (e.g. "$42,000", "18%"). */
  currentValue: string;
  /** Provenance of the current value. */
  currentProvenance: Provenance;
  /** Close without saving. */
  onClose: () => void;
  /**
   * Save the new value org-wide (updates the org profile globally).
   * Called with the formatted value string.
   */
  onSaveOrgWide: (formatted: string) => void;
  /**
   * Save the new value for this opportunity only (local override).
   * Called with the formatted value string.
   */
  onSaveOppOnly: (formatted: string) => void;
  /**
   * Whether the org-wide save path is available.
   * Set to `false` when the field cannot be located in the org profile.
   * Defaults to `true`.
   */
  canSaveOrgWide?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                         */
/* -------------------------------------------------------------------------- */

function detectType(value: string): ValueType {
  const s = value.trim();
  if (/^\$/.test(s)) return "currency";
  if (/^[\d,.]+%$/.test(s)) return "percent";
  if (/^[\d,.\s]+$/.test(s)) return "number";
  return "text";
}

function stripFormat(value: string): string {
  // Remove grouping commas (thousands separators) first so that parseFloat
  // receives e.g. "42000" instead of "42,000" — the latter truncates to 42.
  return value.replace(/,/g, "").replace(/[$%\s]/g, "");
}

function formatTyped(raw: string, type: ValueType): string {
  if (type === "text") return raw.trim();
  const num = parseFloat(stripFormat(raw));
  if (Number.isNaN(num)) return raw.trim();
  switch (type) {
    case "currency":
      return `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    case "percent": {
      const decimals = num >= 100 ? 0 : 1;
      return `${num.toFixed(decimals)}%`;
    }
    case "number":
      return num.toLocaleString();
    default:
      return raw.trim();
  }
}

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Modal for editing a single org-profile field in-place.  Provides two save
 * paths: "Save to org profile" (global) and "Use here only" (opp-level).
 *
 * Fully props-driven — no context or data-module dependencies.
 */
export function OrgFieldEditModal({
  label,
  description,
  currentValue,
  currentProvenance,
  onClose,
  onSaveOrgWide,
  onSaveOppOnly,
  canSaveOrgWide = true,
}: OrgFieldEditModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const valueType = useMemo<ValueType>(
    () => detectType(currentValue),
    [currentValue],
  );
  const initialNumeric = useMemo(() => {
    if (valueType === "text") return currentValue;
    const n = parseFloat(stripFormat(currentValue));
    return Number.isNaN(n) ? currentValue : String(n);
  }, [currentValue, valueType]);
  // Normalize currentValue through the same formatter so integer percents like
  // "18%" don't differ from the formatter's output "18.0%" on the first render.
  const normalizedCurrentValue = useMemo(() => {
    if (valueType === "text") return currentValue.trim();
    const s = currentValue.trim();
    return s ? formatTyped(s, valueType) : "";
  }, [currentValue, valueType]);

  const [draft, setDraft] = useState(initialNumeric);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const trimmed = draft.trim();
  const formatted = trimmed ? formatTyped(trimmed, valueType) : "";
  const unchanged =
    formatted === normalizedCurrentValue || trimmed.length === 0;
  const canSave = !unchanged;

  function handleSaveOrgWide() {
    if (!canSave || !canSaveOrgWide) return;
    onSaveOrgWide(formatted);
    onClose();
  }

  function handleSaveOppOnly() {
    if (!canSave) return;
    onSaveOppOnly(formatted);
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && canSave && canSaveOrgWide) {
      e.preventDefault();
      handleSaveOrgWide();
    }
  }

  return (
    <div
      className="ofe-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${label}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        // Handle keyboard-equivalent of backdrop click (Enter/Space).
        // Escape is intentionally omitted here — it is handled exclusively by
        // the window keydown listener in the useEffect above to prevent
        // onClose from being called twice on a single keypress.
        if (
          (e.key === "Enter" || e.key === " ") &&
          e.target === e.currentTarget
        )
          onClose();
      }}
    >
      <div className="ofe-modal">
        {/* Header */}
        <header className="ofe-head">
          <div>
            <div className="ofe-eyebrow">Edit org-profile value</div>
            <h2>{label}</h2>
          </div>
          <button
            type="button"
            className="ofe-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        {/* Body */}
        <div className="ofe-body">
          {description && <p className="ofe-desc">{description}</p>}

          <div className="ofe-current">
            <span className="ofe-current-lbl">Current value</span>
            <span className="ofe-current-val">{currentValue}</span>
            <ProvenancePill kind={currentProvenance} />
          </div>

          <label className="ofe-input-row">
            <span className="ofe-input-lbl">New value</span>
            <span className={`ofe-input-frame type-${valueType}`}>
              {valueType === "currency" && <span className="ofe-affix">$</span>}
              <input
                ref={inputRef}
                className="ofe-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                inputMode={valueType === "text" ? "text" : "decimal"}
                placeholder={initialNumeric}
              />
              {valueType === "percent" && (
                <span className="ofe-affix suffix">%</span>
              )}
            </span>
          </label>

          <div className="ofe-help" role="note">
            <strong>Save to org profile</strong> updates the value everywhere;
            every opportunity picks it up. <strong>Use here only</strong>{" "}
            applies the value to this opportunity and leaves the org default
            alone.
          </div>
        </div>

        {/* Footer */}
        <footer className="ofe-foot">
          <button type="button" className="ofe-secondary" onClick={onClose}>
            Cancel
          </button>
          <div className="ofe-foot-saves">
            <button
              type="button"
              className="ofe-secondary"
              onClick={handleSaveOppOnly}
              disabled={!canSave}
              title="Apply this value only to this opportunity. Org-profile defaults stay unchanged."
            >
              Use here only
            </button>
            <button
              type="button"
              className="ofe-primary"
              onClick={handleSaveOrgWide}
              disabled={!canSave || !canSaveOrgWide}
              title="Update the org-profile value. Every opportunity that depends on it will pick up the new value."
            >
              Save to org profile
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
